import React from 'react';
import PropTypes from 'prop-types';

/**
 * ProfileTabSVG - A reusable SVG component for profile tabs
 * Based on the Frame 7.svg design with customizable props
 */
const ProfileTabSVG = ({
  width = 480,
  height = 64,
  className = '',
  backgroundColor = 'rgba(255, 255, 255, 0.25)',
  borderColor = '#1FCDFF',
  borderOpacity = 0.2,
  fillColor = '#0A0046',
  fillOpacity = 0.04,
  textColor = '#3C3C43',
  textOpacity = 0.6,
  showProfileIcon = true,
  showLogoutIcon = true,
  profileText = 'Profile',
  logoutText = 'Logout',
  userName = 'User',
  userInitials = 'U',
  showUserName = true,
  onClick,
  style = {},
  ...props
}) => {
  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 480 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`cursor-pointer transition-all duration-300 hover:opacity-80 ${className}`}
      onClick={handleClick}
      style={style}
      {...props}
    >
      {/* Background blur effect */}
      <foreignObject x="-16" y="-16" width="512" height="96">
        <div 
          xmlns="http://www.w3.org/1999/xhtml" 
          style={{
            backdropFilter: 'blur(8px)',
            clipPath: 'url(#bgblur_profile_tab_clip_path)',
            height: '100%',
            width: '100%'
          }}
        />
      </foreignObject>

      {/* Main background */}
      <g data-figma-bg-blur-radius="16">
        <path 
          d="M24 0.5H456C468.979 0.5 479.5 11.0213 479.5 24V40C479.5 52.9787 468.979 63.5 456 63.5H24C11.0213 63.5 0.5 52.9787 0.5 40V24C0.5 11.0213 11.0213 0.5 24 0.5Z" 
          fill="white" 
          fillOpacity="0.25"
        />
        <path 
          d="M24 0.5H456C468.979 0.5 479.5 11.0213 479.5 24V40C479.5 52.9787 468.979 63.5 456 63.5H24C11.0213 63.5 0.5 52.9787 0.5 40V24C0.5 11.0213 11.0213 0.5 24 0.5Z" 
          fill={fillColor} 
          fillOpacity={fillOpacity}
        />
        <path 
          d="M24 0.5H456C468.979 0.5 479.5 11.0213 479.5 24V40C479.5 52.9787 468.979 63.5 456 63.5H24C11.0213 63.5 0.5 52.9787 0.5 40V24C0.5 11.0213 11.0213 0.5 24 0.5Z" 
          stroke="url(#paint0_linear_profile_tab)"
        />
        <path 
          d="M24 0.5H456C468.979 0.5 479.5 11.0213 479.5 24V40C479.5 52.9787 468.979 63.5 456 63.5H24C11.0213 63.5 0.5 52.9787 0.5 40V24C0.5 11.0213 11.0213 0.5 24 0.5Z" 
          stroke={borderColor} 
          strokeOpacity={borderOpacity}
        />

        {/* Profile Avatar with Initials */}
        {showProfileIcon && (
          <g clipPath="url(#clip1_profile_tab)">
            {/* Avatar Circle Background */}
            <rect x="16" y="8" width="48" height="48" rx="24" fill="#E8EAF6"/>
            {/* User Initials Text - Perfectly centered */}
            <text
              x="40"
              y="32"
              fontSize="18"
              fontFamily="Arial, sans-serif"
              fontWeight="600"
              fill="#3F51B5"
              textAnchor="middle"
              dominantBaseline="middle"
              alignmentBaseline="middle"
            >
              {userInitials}
            </text>
          </g>
        )}

        {/* Profile Text */}
        <g clipPath="url(#clip2_profile_tab)">
          <path 
            d="M80.5996 45.6963C80.5996 46.4157 80.4225 47.0355 80.0684 47.5557C79.7197 48.0758 79.2161 48.4743 78.5576 48.751C77.9046 49.0277 77.1243 49.166 76.2168 49.166C75.7741 49.166 75.3507 49.1411 74.9468 49.0913C74.5428 49.0415 74.161 48.9696 73.8013 48.8755C73.4471 48.7759 73.1234 48.6569 72.8301 48.5186V46.6426C73.3226 46.8584 73.8732 47.0549 74.4819 47.2319C75.0907 47.4035 75.7104 47.4893 76.3413 47.4893C76.856 47.4893 77.2821 47.4229 77.6196 47.29C77.9627 47.1517 78.2173 46.958 78.3833 46.709C78.5493 46.4544 78.6323 46.1584 78.6323 45.8208C78.6323 45.4611 78.5355 45.1567 78.3418 44.9077C78.1481 44.6587 77.8548 44.4318 77.4619 44.2271C77.0745 44.0168 76.5876 43.7926 76.001 43.5547C75.6025 43.3942 75.2207 43.2116 74.8555 43.0068C74.4958 42.8021 74.1748 42.5614 73.8926 42.2847C73.6104 42.008 73.3862 41.6815 73.2202 41.3052C73.0597 40.9233 72.9795 40.4751 72.9795 39.9604C72.9795 39.2743 73.1427 38.6877 73.4692 38.2007C73.8013 37.7137 74.2661 37.3402 74.8638 37.0801C75.467 36.82 76.1698 36.6899 76.9722 36.6899C77.6086 36.6899 78.2062 36.7563 78.7651 36.8892C79.3296 37.022 79.883 37.2101 80.4253 37.4536L79.7944 39.0557C79.2964 38.8509 78.8122 38.6877 78.3418 38.5659C77.877 38.4442 77.401 38.3833 76.9141 38.3833C76.4935 38.3833 76.1366 38.4469 75.8433 38.5742C75.55 38.7015 75.3258 38.8813 75.1709 39.1138C75.0215 39.3407 74.9468 39.6118 74.9468 39.9272C74.9468 40.2814 75.0326 40.5802 75.2041 40.8237C75.3812 41.0617 75.6523 41.2803 76.0176 41.4795C76.3883 41.6787 76.8643 41.9001 77.4453 42.1436C78.1149 42.4202 78.6821 42.7108 79.147 43.0151C79.6174 43.3195 79.9771 43.6847 80.2261 44.1108C80.4751 44.5314 80.5996 45.0599 80.5996 45.6963ZM93.1255 39.6367C94.1548 39.6367 94.9295 39.9023 95.4497 40.4336C95.9754 40.9648 96.2383 41.8171 96.2383 42.9902V49H94.2876V43.3389C94.2876 42.6416 94.152 42.1159 93.8809 41.7617C93.6097 41.4076 93.1974 41.2305 92.644 41.2305C91.8693 41.2305 91.3104 41.4795 90.9673 41.9775C90.6297 42.4701 90.4609 43.1867 90.4609 44.1274V49H88.5103V43.3389C88.5103 42.874 88.4494 42.4867 88.3276 42.1768C88.2114 41.8613 88.0316 41.6261 87.7881 41.4712C87.5446 41.3107 87.2375 41.2305 86.8667 41.2305C86.3299 41.2305 85.901 41.3522 85.5801 41.5957C85.2646 41.8392 85.0378 42.1989 84.8994 42.6748C84.7611 43.1452 84.6919 43.7235 84.6919 44.4097V49H82.7412V39.811H84.2603L84.5342 41.0479H84.6421C84.8302 40.7269 85.0627 40.464 85.3394 40.2593C85.6216 40.049 85.9342 39.894 86.2773 39.7944C86.6204 39.6893 86.9774 39.6367 87.3481 39.6367C88.0343 39.6367 88.6126 39.7585 89.083 40.002C89.5534 40.2399 89.9048 40.6051 90.1372 41.0977H90.2866C90.5744 40.5996 90.9756 40.2316 91.4902 39.9937C92.0049 39.7557 92.55 39.6367 93.1255 39.6367ZM102.563 39.6367C103.726 39.6367 104.603 39.894 105.195 40.4087C105.792 40.9233 106.091 41.7257 106.091 42.8159V49H104.705L104.332 47.6968H104.265C104.005 48.0288 103.737 48.3027 103.46 48.5186C103.183 48.7344 102.862 48.8949 102.497 49C102.137 49.1107 101.697 49.166 101.177 49.166C100.629 49.166 100.14 49.0664 99.708 48.8672C99.2764 48.6624 98.936 48.3525 98.687 47.9375C98.438 47.5225 98.3135 46.9967 98.3135 46.3604C98.3135 45.4141 98.6649 44.703 99.3677 44.2271C100.076 43.7511 101.144 43.4883 102.572 43.4385L104.166 43.3804V42.8989C104.166 42.2625 104.016 41.8088 103.717 41.5376C103.424 41.2664 103.009 41.1309 102.472 41.1309C102.013 41.1309 101.567 41.1973 101.136 41.3301C100.704 41.4629 100.284 41.6261 99.874 41.8198L99.2432 40.4419C99.6914 40.2039 100.201 40.0103 100.771 39.8608C101.346 39.7114 101.944 39.6367 102.563 39.6367ZM104.157 44.6089L102.97 44.6504C101.996 44.6836 101.313 44.8496 100.92 45.1484C100.527 45.4473 100.331 45.8568 100.331 46.377C100.331 46.8307 100.466 47.1628 100.737 47.373C101.008 47.5778 101.365 47.6802 101.808 47.6802C102.483 47.6802 103.042 47.4893 103.485 47.1074C103.933 46.7201 104.157 46.1528 104.157 45.4058V44.6089ZM113.479 39.6367C113.634 39.6367 113.803 39.645 113.985 39.6616C114.168 39.6782 114.326 39.7004 114.458 39.728L114.276 41.5542C114.16 41.521 114.016 41.4961 113.844 41.4795C113.678 41.4629 113.529 41.4546 113.396 41.4546C113.047 41.4546 112.715 41.5127 112.4 41.6289C112.084 41.7396 111.805 41.9111 111.562 42.1436C111.318 42.3704 111.127 42.6554 110.989 42.9985C110.85 43.3416 110.781 43.7401 110.781 44.1938V49H108.822V39.811H110.35L110.615 41.4297H110.707C110.889 41.1032 111.116 40.8044 111.387 40.5332C111.658 40.262 111.968 40.0462 112.317 39.8857C112.671 39.7197 113.058 39.6367 113.479 39.6367ZM119.58 47.5889C119.835 47.5889 120.086 47.5667 120.335 47.5225C120.584 47.4727 120.811 47.4146 121.016 47.3481V48.8257C120.8 48.9198 120.521 49 120.178 49.0664C119.835 49.1328 119.478 49.166 119.107 49.166C118.587 49.166 118.119 49.0802 117.704 48.9087C117.289 48.7316 116.96 48.43 116.716 48.0039C116.473 47.5778 116.351 46.9884 116.351 46.2358V41.2969H115.098V40.4253L116.442 39.7363L117.082 37.769H118.31V39.811H120.941V41.2969H118.31V46.2109C118.31 46.6758 118.426 47.0216 118.659 47.2485C118.891 47.4754 119.198 47.5889 119.58 47.5889ZM127.499 49V36.8643H129.491V49H127.499ZM135.584 49.166C134.488 49.166 133.608 48.7676 132.944 47.9707C132.286 47.1683 131.957 45.9868 131.957 44.4263C131.957 42.8491 132.291 41.6566 132.961 40.8486C133.636 40.0407 134.524 39.6367 135.625 39.6367C136.09 39.6367 136.497 39.7004 136.846 39.8276C137.194 39.9494 137.493 40.1154 137.742 40.3257C137.997 40.536 138.213 40.7712 138.39 41.0312H138.481C138.453 40.8597 138.42 40.6134 138.381 40.2925C138.348 39.966 138.332 39.6644 138.332 39.3877V36.084H140.291V49H138.763L138.415 47.7466H138.332C138.166 48.0122 137.955 48.2529 137.701 48.4688C137.452 48.679 137.153 48.8478 136.804 48.9751C136.461 49.1024 136.054 49.166 135.584 49.166ZM136.132 47.5806C136.967 47.5806 137.557 47.3398 137.9 46.8584C138.243 46.377 138.42 45.6548 138.431 44.6919V44.4346C138.431 43.4053 138.265 42.6167 137.933 42.0688C137.601 41.5155 136.995 41.2388 136.115 41.2388C135.412 41.2388 134.876 41.5238 134.505 42.0938C134.14 42.6582 133.957 43.4468 133.957 44.4595C133.957 45.4722 134.14 46.2469 134.505 46.7837C134.876 47.3149 135.418 47.5806 136.132 47.5806Z" 
            fill="#3C3C43" 
            fillOpacity="0.6"
          />
        </g>

        {/* User Name Text */}
        {showUserName && (
          <text
            x="70"
            y="25"
            fontSize="16"
            fontFamily="Arial, sans-serif"
            fontWeight="600"
            fill="#0A0046"
            textAnchor="start"
            dominantBaseline="middle"
          >
            {userName}
          </text>
        )}

        {/* Logout Icon */}
        {showLogoutIcon && (
          <g clipPath="url(#clip3_profile_tab)">
            <path 
              d="M457.586 25.5858C458.367 24.8047 459.633 24.8047 460.414 25.5858C461.195 26.3668 461.195 27.6332 460.414 28.4142L459.621 29.2071L456.793 26.3787L457.586 25.5858Z" 
              fill={fillColor}
            />
            <path 
              d="M455.379 27.7929L447 36.1716V39H449.828L458.207 30.6213L455.379 27.7929Z" 
              fill={fillColor}
            />
          </g>
        )}
      </g>

      {/* Definitions */}
      <defs>
        <clipPath id="bgblur_profile_tab_clip_path" transform="translate(16 16)">
          <path d="M24 0.5H456C468.979 0.5 479.5 11.0213 479.5 24V40C479.5 52.9787 468.979 63.5 456 63.5H24C11.0213 63.5 0.5 52.9787 0.5 40V24C0.5 11.0213 11.0213 0.5 24 0.5Z"/>
        </clipPath>
        <linearGradient id="paint0_linear_profile_tab" x1="240" y1="0" x2="240" y2="64" gradientUnits="userSpaceOnUse">
          <stop stopColor="white" stopOpacity="0.01"/>
          <stop offset="1" stopColor="white"/>
        </linearGradient>
        <clipPath id="clip1_profile_tab">
          <rect width="179" height="48" fill="white" transform="translate(16 8)"/>
        </clipPath>
        <clipPath id="clip2_profile_tab">
          <rect width="70" height="16" fill="white" transform="translate(72 34)"/>
        </clipPath>
        <clipPath id="clip3_profile_tab">
          <rect width="116" height="20" fill="white" transform="translate(348 22)"/>
        </clipPath>
      </defs>
    </svg>
  );
};

ProfileTabSVG.propTypes = {
  /** Width of the SVG component */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Height of the SVG component */
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Additional CSS classes */
  className: PropTypes.string,
  /** Background color */
  backgroundColor: PropTypes.string,
  /** Border color */
  borderColor: PropTypes.string,
  /** Border opacity */
  borderOpacity: PropTypes.number,
  /** Fill color */
  fillColor: PropTypes.string,
  /** Fill opacity */
  fillOpacity: PropTypes.number,
  /** Text color */
  textColor: PropTypes.string,
  /** Text opacity */
  textOpacity: PropTypes.number,
  /** Show profile icon */
  showProfileIcon: PropTypes.bool,
  /** Show logout icon */
  showLogoutIcon: PropTypes.bool,
  /** Profile text */
  profileText: PropTypes.string,
  /** Logout text */
  logoutText: PropTypes.string,
  /** User name to display */
  userName: PropTypes.string,
  /** User initials to display in avatar (e.g., "MN" for Mathew Norm) */
  userInitials: PropTypes.string,
  /** Show user name */
  showUserName: PropTypes.bool,
  /** Click handler */
  onClick: PropTypes.func,
  /** Additional styles */
  style: PropTypes.object,
};

export default ProfileTabSVG;
